import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useLocation, useRoute } from "wouter";
import { apiSend } from "../lib/customFetch";
import {
  API_UNAUTHORIZED_EVENT,
  SHELL_SESSION_EXPIRED_EVENT,
  currentAuthenticationGeneration,
  unauthorizedGeneration,
} from "../lib/session-events";
import { useFeatures, type FeatureInfo } from "../lib/useFeatures";
import { ROLLE_TEKST, useMe } from "../lib/useMe";
import { Footer } from "./Footer";
import { NotificationCenter } from "./NotificationCenter";
import { Skeleton } from "./Spinner";
import styles from "./AppShell.module.css";

/**
 * Navigationen har to kilder:
 *  - CORE-sider (fast produktflade, fx Medarbejdere for admin) — altid der.
 *  - Features fra /api/features-svaret — KUN enabled features optræder
 *    (main → hovednav, settings → sektion, none → udelades). Ingen
 *    hardcodede FEATURE-links; core-links er ikke features.
 * Uautentificeret → redirect til /login (og nav uden links).
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { features, loading, error } = useFeatures();
  const { me, loading: meLoading, error: meError } = useMe();
  const [location, navigate] = useLocation();
  const pathname = location.split(/[?#]/, 1)[0] ?? location;
  const previousPathnameRef = useRef(pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const meRef = useRef(me);

  useLayoutEffect(() => {
    meRef.current = me;
  }, [me]);

  // Uautentificeret tilgang til shell-sider → /login.
  useEffect(() => {
    if (!meLoading && meError === "unauthorized") {
      navigate("/login", { replace: true });
    }
  }, [meLoading, meError, navigate]);

  useEffect(() => {
    const onUnauthorized = (event: Event) => {
      if (
        meRef.current === null ||
        unauthorizedGeneration(event) !== currentAuthenticationGeneration()
      ) {
        return;
      }
      window.dispatchEvent(new Event(SHELL_SESSION_EXPIRED_EVENT));
      navigate("/login?session=udloebet", { replace: true });
    };
    window.addEventListener(API_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(API_UNAUTHORIZED_EVENT, onUnauthorized);
  }, [navigate]);

  const main = features.filter((f) => f.navPlacement === "main");
  const settings = features.filter((f) => f.navPlacement === "settings");
  const currentLabel = useMemo(() => {
    const coreLabels: Record<string, string> = {
      "/": "Overblik",
      "/medarbejdere": "Medarbejdere",
      "/onboarding": "Opsætning",
      "/indstillinger": "Indstillinger",
      "/enheder": "Enheder",
      "/konto": "Konto",
    };
    const core = coreLabels[pathname];
    if (core !== undefined) return core;
    return features.find((feature) => `/${feature.key}` === pathname)?.name ?? "AppSmeden";
  }, [features, pathname]);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      window.scrollTo({ top: 0, left: 0 });
    }
    previousPathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const logout = () => {
    void apiSend("POST", "/api/logout").finally(() => navigate("/login"));
  };

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main-content">
        Spring til hovedindhold
      </a>
      <aside
        className={styles.sidebar}
        onKeyDown={(event) => {
          if (event.key !== "Escape" || !mobileOpen) return;
          setMobileOpen(false);
          menuButtonRef.current?.focus();
        }}
      >
        <div className={styles.mobileHeader}>
          <Link href="/" className={styles.brand} aria-label="AppSmeden — overblik">
            app<span>smeden</span>
          </Link>
          <span className={styles.mobileContext}>{currentLabel}</span>
          <button
            ref={menuButtonRef}
            type="button"
            className={styles.menuToggle}
            aria-expanded={mobileOpen}
            aria-controls="app-navigation-panel"
            aria-label={`${mobileOpen ? "Luk" : "Menu"}${unreadCount > 0 ? ` — ${String(unreadCount)} ulæste notifikationer` : ""}`}
            onClick={() => setMobileOpen((open) => !open)}
          >
            <span aria-hidden="true" className={styles.menuIcon}>
              {mobileOpen ? "×" : "☰"}
            </span>
            {mobileOpen ? "Luk" : "Menu"}
            {unreadCount > 0 && (
              <span
                className={styles.mobileUnreadBadge}
                aria-hidden="true"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
          <span className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {unreadCount > 0 ? `${String(unreadCount)} ulæste notifikationer` : ""}
          </span>
        </div>
        <div
          id="app-navigation-panel"
          className={`${styles.mobilePanel} ${mobileOpen ? styles.mobilePanelOpen ?? "" : ""}`}
        >
          <nav className={styles.nav} aria-label="Hovednavigation">
            {(loading || meLoading) && (
              <>
                <Skeleton height="2.25rem" />
                <Skeleton height="2.25rem" width="80%" />
              </>
            )}
            {!meLoading && meError === "unauthorized" && (
              <p className={styles.navEmpty}>
                Ikke logget ind. <Link href="/login">Log ind</Link> eller{" "}
                <Link href="/signup">opret virksomhed</Link>.
              </p>
            )}
            {!loading && error === "failed" && (
              <p className={styles.navEmpty}>Kunne ikke hente navigationen.</p>
            )}
            {!meLoading && me !== null && (
              <>
                {(me.rolle === "ejer" || me.rolle === "administrator") && (
                  <>
                    <NavLink href="/" label="Overblik" />
                    <NavLink href="/medarbejdere" label="Medarbejdere" />
                    <NavLink href="/onboarding" label="Opsætning" />
                    <NavLink href="/indstillinger" label="Indstillinger" />
                    <NavLink href="/enheder" label="Enheder" />
                  </>
                )}
                {me.rolle === "bogholder" && (
                  <NavLink href="/konto" label="Konto" />
                )}
                {!loading &&
                  error === null &&
                  main
                    .filter(
                      (feature) =>
                        me.rolle !== "bogholder" ||
                        feature.key === "clockin" ||
                        feature.key === "koersel",
                    )
                    .map((f) => (
                    <FeatureNavLink
                      key={f.key}
                      feature={f}
                      href={
                        me.rolle === "medarbejder" && f.key === "clockin"
                          ? "/stemple"
                          : me.rolle === "bogholder" && f.key === "clockin"
                            ? "/clockin?fane=rapport"
                          : `/${f.key}`
                      }
                    />
                  ))}
                {!loading &&
                  error === null &&
                  (me.rolle === "ejer" || me.rolle === "administrator") &&
                  settings.length > 0 && (
                  <>
                    <p className={styles.sectionLabel}>Indstillinger</p>
                    {settings.map((f) => (
                      <FeatureNavLink key={f.key} feature={f} />
                    ))}
                  </>
                )}
              </>
            )}
          </nav>
          {me !== null && (
            <NotificationCenter
              layout="shell"
              enabledFeatureKeys={features.map((feature) => feature.key)}
              onUnreadCountChange={setUnreadCount}
            />
          )}
          {me !== null && (
            <div className={styles.identity}>
              <p className={styles.identityName}>{me.navn}</p>
              <p className={styles.identityTenant}>
                {me.tenantNavn} · {ROLLE_TEKST[me.rolle]}
              </p>
              <button type="button" className={styles.logout} onClick={logout}>
                Log ud
              </button>
            </div>
          )}
        </div>
      </aside>
      <main id="main-content" className={styles.main} tabIndex={-1}>
        {children}
        <Footer />
      </main>
    </div>
  );
}

function NavLink({ href, label }: { href: string; label: string }) {
  const [isActive] = useRoute(href.split("?", 1)[0] ?? href);
  const classes = [styles.navLink, isActive ? styles.navLinkActive : null]
    .filter(Boolean)
    .join(" ");
  return (
    <Link
      href={href}
      className={classes}
      aria-current={isActive ? "page" : undefined}
    >
      {label}
    </Link>
  );
}

function FeatureNavLink({
  feature,
  href = `/${feature.key}`,
}: {
  feature: FeatureInfo;
  href?: string;
}) {
  return <NavLink href={href} label={feature.name} />;
}
